/**
 * Klaviyo Back-in-Stock Modal Handler
 *
 * This script handles the Back-in-Stock notification functionality
 * for out-of-stock products using Klaviyo's API.
 *
 * Features:
 * - Shows/hides "Notify Me" button based on variant availability
 * - Opens modal when button is clicked
 * - Populates modal with product info and unavailable variants
 * - Submits email subscriptions to Klaviyo
 */

(function() {
  'use strict';

  /**
   * Initialize Back-in-Stock functionality
   */
  function initKlaviyoBIS() {
    const bisButton = document.querySelector('[data-product-bis-button]');
    const addToCartButton = document.querySelector('[data-product-add-to-cart-button]');
    const klaviyoModal = document.querySelector('.klaviyo-bis-modal');

    if (!bisButton || !klaviyoModal) {
      return; // Exit if required elements don't exist
    }

    // Get product data from variant picker
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) {
      return;
    }

    let productData = null;
    let currentVariant = null;

    // Get product data (needs to be loaded from product page)
    async function loadProductData() {
      const productHandle = variantPicker.getAttribute('handle');
      if (!productHandle) return null;

      try {
        const response = await fetch(`/products/${productHandle}.js`);
        const data = await response.json();
        return data;
      } catch (error) {
        console.error('Error loading product data:', error);
        return null;
      }
    }

    // Initialize product data
    loadProductData().then(data => {
      productData = data;
      updateButtonState(variantPicker.selectedVariant);
    });

    /**
     * Update button visibility based on variant availability
     */
    function updateButtonState(variant) {
      if (!variant) return;

      currentVariant = variant;

      if (variant.available) {
        // Product is available - show Add to Cart, hide Notify Me
        if (addToCartButton) {
          addToCartButton.disabled = false;
          addToCartButton.style.display = '';
          addToCartButton.removeAttribute('hidden');
        }
        bisButton.disabled = true;
        bisButton.style.display = 'none';
        bisButton.setAttribute('hidden', '');
      } else {
        // Product is out of stock - hide Add to Cart, show Notify Me
        if (addToCartButton) {
          addToCartButton.disabled = true;
          addToCartButton.style.display = 'none';
          addToCartButton.setAttribute('hidden', '');
        }
        bisButton.disabled = false;
        bisButton.style.display = '';
        bisButton.removeAttribute('hidden');
      }
    }

    /**
     * Open and populate the modal
     */
    function openModal() {
      if (!productData || !currentVariant) return;

      // Open modal
      klaviyoModal.setAttribute('open', 'true');
      klaviyoModal.classList.add('fadein');
      document.body.classList.add('fadein');

      // Update modal title
      const modalTitle = klaviyoModal.querySelector('.klaviyo-modal-title');
      if (modalTitle) {
        modalTitle.textContent = productData.title;
      }

      // Populate variant dropdown with only unavailable variants
      const variantSelect = klaviyoModal.querySelector('.selectpicker');
      if (variantSelect && productData.variants) {
        let options = '';
        productData.variants.forEach(variant => {
          if (!variant.available) {
            const isSelected = currentVariant && currentVariant.id === variant.id;
            const variantTitle = variant.title === 'Default Title' ? productData.title : variant.title;
            options += `<option value="${variant.id}" ${isSelected ? 'selected' : ''}>${variantTitle}</option>`;
          }
        });
        variantSelect.innerHTML = options;
      }
    }

    /**
     * Close the modal
     */
    function closeModal() {
      klaviyoModal.removeAttribute('open');
      klaviyoModal.classList.remove('fadein');
      document.body.classList.remove('fadein');
    }

    // Handle button click
    bisButton.addEventListener('click', function(e) {
      e.preventDefault();
      openModal();
    });

    // Handle close button click
    const closeButton = klaviyoModal.querySelector('[data-action="close"]');
    if (closeButton) {
      closeButton.addEventListener('click', closeModal);
    }

    // Handle modal overlay click
    klaviyoModal.addEventListener('click', function(e) {
      if (e.target === klaviyoModal) {
        closeModal();
      }
    });

    // Handle form submission
    const klaviyoForm = klaviyoModal.querySelector('.klaviyo-form');
    const klaviyoButton = klaviyoModal.querySelector('.klaviyo-button');

    if (klaviyoButton && klaviyoForm) {
      klaviyoButton.addEventListener('click', function(e) {
        e.preventDefault();
        submitToKlaviyo();
      });
    }

    /**
     * Submit email to Klaviyo Back-in-Stock API
     *
     * This implementation:
     * 1. Subscribes user to back-in-stock notifications for the specific variant
     * 2. Subscribes user to email marketing list (not just back-in-stock only)
     * 3. Identifies the user in Klaviyo for tracking
     */
    async function submitToKlaviyo() {
      const emailInput = klaviyoForm.querySelector('input[name="email"]');
      const variantSelect = klaviyoForm.querySelector('select[name="variant"]');
      const apiKeyInput = klaviyoForm.querySelector('input[name="a"]');

      if (!emailInput || !emailInput.value) {
        alert('Please enter your email address');
        return;
      }

      const email = emailInput.value;
      const variantId = variantSelect ? variantSelect.value : currentVariant.id;
      const apiKey = apiKeyInput ? apiKeyInput.value : '';

      if (!apiKey) {
        console.error('Klaviyo API key not configured');
        alert('Configuration error. Please contact support.');
        return;
      }

      // Disable button during submission
      klaviyoButton.disabled = true;
      klaviyoButton.textContent = 'Submitting...';

      try {
        // Get variant object for more data
        const selectedVariant = productData.variants.find(v => v.id == variantId);

        // Create the variant ID in Klaviyo's format: $shopify:::$default:::variantId
        const klaviyoVariantId = `$shopify:::$default:::${variantId}`;

        // 1. Subscribe to Back-in-Stock notifications using the client API
        const bisResponse = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${apiKey}`, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'content-type': 'application/json',
            'revision': '2024-07-15'
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: email
                    }
                  }
                }
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: klaviyoVariantId
                  }
                }
              }
            }
          })
        });

        // Log response for debugging
        console.log('BIS Response status:', bisResponse.status);

        // Check if response is ok or accepted (202)
        if (!bisResponse.ok && bisResponse.status !== 202) {
          const errorData = await bisResponse.json().catch(() => null);
          console.error('BIS Error:', errorData);
          throw new Error(`Back-in-Stock API returned ${bisResponse.status}`);
        }

        // 2. Subscribe to email marketing list (not just back-in-stock only)
        // This is the key difference - adds them to general marketing emails
        // const subscriptionResponse = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${apiKey}`, {
        //   method: 'POST',
        //   headers: {
        //     'accept': 'application/json',
        //     'content-type': 'application/json',
        //     'revision': '2024-07-15'
        //   },
        //   body: JSON.stringify({
        //     data: {
        //       type: 'subscription',
        //       attributes: {
        //         profile: {
        //           data: {
        //             type: 'profile',
        //             attributes: {
        //               email: email
        //             }
        //           }
        //         }
        //       }
        //     }
        //   })
        // });

        // console.log('Subscription Response status:', subscriptionResponse.status);

        // if (!subscriptionResponse.ok && subscriptionResponse.status !== 202) {
        //   console.warn('Email subscription returned non-202 status:', subscriptionResponse.status);
        //   // Don't throw error - back-in-stock subscription succeeded
        // }

        // 3. Identify the user for better tracking (optional but recommended)
        if (typeof _learnq !== 'undefined') {
          _learnq.push(['identify', {
            '$email': email
          }]);
        }

        // Success - show message
        const container = klaviyoModal.querySelector('#container');
        container.innerHTML = `
          <button type="button" data-action="close" class="close klaviyo-bis-close">×</button>
          <div class="alert alert-success" style="margin-top: 20px;">
            <h4>Success!</h4>
            <p>You'll receive an email notification when this product is back in stock.</p>
          </div>
        `;

        // Re-attach close button event
        const newCloseButton = container.querySelector('[data-action="close"]');
        if (newCloseButton) {
          newCloseButton.addEventListener('click', closeModal);
        }

        // Auto-close after 3 seconds
        setTimeout(() => {
          closeModal();
          // Reset form for next time
          location.reload();
        }, 3000);

      } catch (error) {
        console.error('Error submitting to Klaviyo:', error);
        alert('There was an error processing your request. Please try again.');
        klaviyoButton.disabled = false;
        klaviyoButton.textContent = 'Notify Me';
      }
    }

    // Listen for variant changes
    document.addEventListener('variant:changed', function(event) {
      if (event.detail && event.detail.variant) {
        updateButtonState(event.detail.variant);
      }
    });

    // Also listen on the form element
    const productForm = document.querySelector('product-form, form[is="product-form"]');
    if (productForm) {
      productForm.addEventListener('variant:changed', function(event) {
        if (event.detail && event.detail.variant) {
          updateButtonState(event.detail.variant);
        }
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initKlaviyoBIS);
  } else {
    initKlaviyoBIS();
  }

})();
